import { AxiosResponse } from 'axios';
import { BaseService } from './base.service';
import { GetAthleteTrainingLogPayload, TeamPayload } from '../models/service.models';
import { StatsResponse } from '../models/stats.models';
import { TeamDetails, TeamJoinRequestDecision, TeamJoinRequestSubmitPayload } from '../models/team.models';
import { User } from '../models/user.models';

export default class TeamsService extends BaseService {
  public async getTeams() {
    return this.axios.get(`${this.backendUrl}/teams`);
  }

  public async createTeam(payload: TeamPayload) {
    return this.axios.post(`${this.backendUrl}/teams`, payload);
  }

  public async deleteTeam(id: string) {
    return this.axios.delete(`${this.backendUrl}/team/${id}`);
  }

  public async keepTeam(id: string | number) {
    return this.axios.post(`${this.backendUrl}/team/${id}/keep`);
  }

  public async leaveTeam(id: string) {
    return this.axios.delete(`${this.backendUrl}/athlete-team/${id}`);
  }

  public async editTeamDetails(id: string, teamDetails: TeamDetails) {
    const formData = new FormData();
    formData.append('name', teamDetails.name);
    formData.append('color', teamDetails.color);
    if (teamDetails.description) {
      formData.append('description', teamDetails.description);
    }
    return this.axios.put(`${this.backendUrl}/team/${id}`, formData);
  }

  public async getTeamDetails(id: string) {
    return this.axios.get(`${this.backendUrl}/team/${id}`);
  }

  /**
   * Fetches active team members.
   * Response `data` is a flat array of active members (coaches get `guardians` on each).
   * Retired athletes are fetched separately via `getRetiredAthletes` (coach only).
   */
  public async getAllTeamMembers(id: string) {
    return this.axios.get(`${this.backendUrl}/team/${id}/members`);
  }

  /** Fetches retired (inactive) athletes for a team. Coach only. */
  public async getRetiredAthletes(id: string) {
    return this.axios.get(`${this.backendUrl}/team/${id}/retired-athletes`);
  }

  /** Fetches a single team member (coach only). */
  public async getTeamMember(teamId: string, memberId: string) {
    return this.axios.get(`${this.backendUrl}/team/${teamId}/member/${memberId}`);
  }

  /**
   * Coach fetches athlete training log stats.
   * POST /v2/team/<team_id>/athlete/<member_id>/stats
   */
  public async getAthleteTrainingLog(
    teamId: string,
    memberId: string,
    payload: GetAthleteTrainingLogPayload
  ): Promise<AxiosResponse<{ data: StatsResponse }>> {
    const user: User = JSON.parse(window.localStorage.getItem('user') as string);
    return this.axios.post(`${this.backendUrl}/v2/team/${teamId}/athlete/${memberId}/stats`, payload, {
      headers: {
        'x-access-token': user.token,
        'Measurement-System': user.measurementSystem,
      },
    });
  }

  public async removeTeamMember(teamId: string, memberId: string) {
    return this.axios.delete(`${this.backendUrl}/team/${teamId}/member/${memberId}`);
  }

  public async getTeamInvitations(teamId: string) {
    return this.axios.get(`${this.backendUrl}/team/${teamId}/invitations`);
  }

  public async inviteAthletesToTeam(teamId: string, athletes: string[]) {
    return this.axios.post(`${this.backendUrl}/team/${teamId}/invitations`, { athletes });
  }

  public async deactivateAthleteInvitation(teamId: string, invitationId: string) {
    return this.axios.delete(`${this.backendUrl}/team/${teamId}/invitation/${invitationId}`);
  }

  public async postTeamInviteCode(teamId: string) {
    return this.axios.post(`${this.backendUrl}/team/${teamId}/invite-code`);
  }

  public async deleteTeamInviteCode(teamId: string) {
    return this.axios.delete(`${this.backendUrl}/team/${teamId}/invite-code`);
  }

  public async postTeamJoinRequest(payload: TeamJoinRequestSubmitPayload) {
    return this.axios.post(`${this.backendUrl}/team/join-request`, payload);
  }

  public async getTeamJoinRequests(teamId: string) {
    return this.axios.get(`${this.backendUrl}/team/${teamId}/join-requests`);
  }

  public async putTeamJoinRequestDecision(teamId: string, joinRequestId: number, decision: TeamJoinRequestDecision) {
    return this.axios.put(`${this.backendUrl}/team/${teamId}/join-request/${joinRequestId}`, { decision });
  }

  /**
   * Updates a team member (coach only).
   * PUT /team/{id}/member/{id} — at least one of roleId or active is required.
   * active: false retires an athlete; true unretires. Only athletes can be retired.
   */
  public async updateAthlete(
    teamId: string,
    athleteId: string,
    payload: { roleId?: string | number; active?: boolean }
  ) {
    return this.axios.put(`${this.backendUrl}/team/${teamId}/member/${athleteId}`, payload);
  }

  public async createBasicTeam(teamDetails: TeamDetails) {
    return this.axios.post(`${this.backendUrl}/basic-teams`, teamDetails);
  }

  /** Founding Unlimited squad for the owner exception. Members are included. */
  public async createUnlimitedTeam(teamDetails: { teamName: string; teamColor: string; teamDescription: string }) {
    return this.axios.post<{ data: { teamId: number } }>(`${this.backendUrl}/teams/unlimited`, teamDetails);
  }

  public async isEligibleForBasicTeam() {
    return this.axios.get(`${this.backendUrl}/basic-teams/eligibility`);
  }

  public async getChatChannels(teamId: string) {
    return this.axios.get(`${this.backendUrl}/team/${teamId}/chat/channels`);
  }

  public async createChatChannel(teamId: string, name: string, memberIds: number[]) {
    return this.axios.post(`${this.backendUrl}/team/${teamId}/chat/channels`, { name, member_ids: memberIds });
  }

  public async deleteChatChannel(teamId: string, channelId: number) {
    return this.axios.delete(`${this.backendUrl}/team/${teamId}/chat/channel/${channelId}`);
  }

  public async getChatSession(teamId: string, channelId: number) {
    return this.axios.get(`${this.backendUrl}/team/${teamId}/chat/channel/${channelId}/session`);
  }

  public async getChatEmbedToken() {
    return this.axios.get(`${this.backendUrl}/chat/embed-token`);
  }
}
